import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock } from "lucide-react";
import { User, loadUsers, displayName, initials, avatarColor } from "./user-display";

interface Props {
    categoryId: string;
    // already-loaded access level from the parent's category record — the banner
    // only fetches member details when it's "restricted"
    accessLevel?: string;
    // bump to refetch the member list (e.g. after the visibility modal saves)
    refreshKey?: number | string;
    // when the page is still an unpublished draft, phrase visibility as future-tense
    draft?: boolean;
}

// Banner shown on restricted categories (and their folders/pages) listing who
// can currently see the content: category owners + invited people.
export default function LimitedAccessBanner({ categoryId, accessLevel, refreshKey, draft }: Props) {
    const [memberIds, setMemberIds] = useState<string[] | null>(null);
    const [users, setUsers] = useState<Map<string, User>>(new Map());
    const restricted = accessLevel === "restricted";

    useEffect(() => {
        if (!restricted) return;
        setMemberIds(null);
        Promise.all([
            axios.get(`/api/manuals/categories/${categoryId}`),
            loadUsers(),
        ]).then(([catRes, userList]) => {
            setUsers(new Map(userList.map(u => [u.id, u])));
            if (catRes.data.success) {
                const item = catRes.data.item;
                // owners first, then invited people, deduped
                const ids: string[] = [];
                [...(item.ownerUserIds || []), ...(item.allowedUserIds || [])]
                    .forEach((id: string) => { if (!ids.includes(id)) ids.push(id); });
                setMemberIds(ids);
            }
        }).catch(() => { /* banner is informational — fail silently */ });
    }, [categoryId, restricted, refreshKey]);

    if (!restricted || memberIds === null) return null;

    return (
        <div className="m-limited-banner">
            <span className="m-limited-banner-lead">
                <Lock size={13} />
                {draft
                    ? <>Is in a <strong>limited-access</strong> category — will be visible to Admins and:</>
                    : <><strong>Limited access</strong> — visible to admins and:</>}
            </span>
            <span className="m-limited-banner-people">
                {memberIds.length === 0 && <em>no one has been invited yet</em>}
                {memberIds.map(id => {
                    const u = users.get(id);
                    const label = u ? displayName(u) : id.slice(0, 8);
                    return (
                        <span key={id} className="m-limited-banner-chip">
                            <span
                                className="m-comment-avatar sm"
                                style={{ background: avatarColor(id), width: 18, height: 18, fontSize: 8 }}
                            >
                                {initials(label)}
                            </span>
                            {label}
                        </span>
                    );
                })}
            </span>
        </div>
    );
}
